import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const safeReturnPath = (value) => {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\')
  ) {
    return '/app';
  }

  try {
    const parsed = new URL(value, window.location.origin);
    return parsed.origin === window.location.origin
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : '/app';
  } catch (_error) {
    return '/app';
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);
  const { login, completeNewPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = safeReturnPath(location.state?.returnTo);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        navigate(returnTo, { replace: true });
      } else if (result.requiresNewPassword) {
        setRequiresNewPassword(true);
      } else {
        setError(result.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!givenName || !familyName) {
      setError('First name and last name are required');
      return;
    }

    // Validate new password
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    // Check password requirements
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setError('Password must contain uppercase, lowercase, numbers, and special characters');
      return;
    }

    setLoading(true);

    try {
      const userAttributes = {
        given_name: givenName,
        family_name: familyName
      };

      const result = await completeNewPassword(newPassword, userAttributes);

      if (result.success) {
        navigate(returnTo, { replace: true });
      } else {
        setError(result.message || 'Failed to set new password. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <img src="/chulas.png" alt="Chula's Logo" />
        </div>
        <div className="login-header">
          <h1>Chula's Admin Portal</h1>
          <p>{requiresNewPassword ? 'Set your new password' : 'Sign in to manage your restaurant'}</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!requiresNewPassword ? (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="admin@chulas.com"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleNewPasswordSubmit} className="login-form">
            <div className="info-message">
              <p>Your temporary password has been verified. Please complete your profile and set a new password to continue.</p>
            </div>

            <div className="form-group">
              <label htmlFor="givenName">First Name</label>
              <input
                type="text"
                id="givenName"
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
                required
                autoComplete="given-name"
                placeholder="Enter your first name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="familyName">Last Name</label>
              <input
                type="text"
                id="familyName"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                required
                autoComplete="family-name"
                placeholder="Enter your last name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Enter new password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Confirm new password"
                disabled={loading}
              />
            </div>

            <div className="password-requirements">
              <p>Password must contain:</p>
              <ul>
                <li>At least 12 characters</li>
                <li>Uppercase and lowercase letters</li>
                <li>Numbers</li>
                <li>Special characters (!@#$%^&*)</li>
              </ul>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Setting Password...' : 'Set New Password'}
            </button>
          </form>
        )}

        <div className="login-footer">
          <p>Admin access only. Contact your administrator for credentials.</p>
        </div>
      </div>
    </div>
  );
};

export { safeReturnPath };
export default Login;
